import { useEffect, useRef, useState } from "react";

import type { IDropdownPosition, IUseAnchoredPortalResult } from "./types";

export const useAnchoredPortal = (isOpen: boolean, onDismiss: () => void): IUseAnchoredPortalResult => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const onDismissRef = useRef<VoidFunction>(onDismiss);
    const [position, setPosition] = useState<IDropdownPosition | null>(null);
    onDismissRef.current = onDismiss;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const updatePosition = (): void => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) {
                return;
            }
            setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
        };

        const handlePointerDown = (event: PointerEvent): void => {
            const target = event.target as Node;
            if (anchorRef.current?.contains(target) || portalRef.current?.contains(target)) {
                return;
            }
            onDismissRef.current();
        };

        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        document.addEventListener("pointerdown", handlePointerDown, true);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
            document.removeEventListener("pointerdown", handlePointerDown, true);
        };
    }, [isOpen]);

    return { position, anchorRef, portalRef };
};
